import type {
  MeetingMinutesAgendaItem,
  StoredMeetingSummary,
  TranscriptLine,
} from '../types/minutes.types.ts';

const DECISION_RE =
  /(kararlaştırıldı|karar aldık|karar verildi|onaylandı|kabul edildi|karar:)/i;
const ACTION_RE =
  /(yapılacak|aksiyon|görev|sorumlu|TODO|to[\s-]?do|takip edilecek)/i;

export interface BuildMeetingMinutesInput {
  meetingId: string;
  meetingCode: string;
  title: string;
  hostName: string;
  participants: string[];
  startedAt: number;
  endedAt: number;
  agenda: MeetingMinutesAgendaItem[];
  transcript: TranscriptLine[];
  minutesEnabled: boolean;
  notes?: string;
}

function extractLines(transcript: TranscriptLine[], re: RegExp): string[] {
  const out: string[] = [];
  for (const line of transcript) {
    if (!re.test(line.text)) continue;
    const trimmed = line.text.trim();
    if (trimmed && !out.includes(trimmed)) out.push(trimmed);
  }
  return out;
}

function formatClock(ts: number): string {
  return new Date(ts).toLocaleString('tr-TR', {
    dateStyle: 'long',
    timeStyle: 'short',
  });
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m} dk ${s.toString().padStart(2, '0')} sn`;
}

export function buildMeetingMinutes(input: BuildMeetingMinutesInput): StoredMeetingSummary {
  const durationSeconds = Math.max(
    0,
    Math.floor((input.endedAt - input.startedAt) / 1000)
  );
  const decisions = extractLines(input.transcript, DECISION_RE);
  const actionItems = extractLines(input.transcript, ACTION_RE);

  const agendaBlock =
    input.agenda.length > 0
      ? input.agenda
          .slice()
          .sort((a, b) => a.order - b.order)
          .map((a, i) => `${i + 1}. ${a.title} (${a.duration} dk)`)
          .join('\n')
      : '_Gündem maddesi yok._';

  const transcriptBlock =
    input.transcript.length > 0
      ? input.transcript
          .map((l) => {
            const t = new Date(l.at).toLocaleTimeString('tr-TR', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            });
            return `**[${t}] ${l.speaker}:** ${l.text}`;
          })
          .join('\n\n')
      : input.minutesEnabled
        ? '_Konuşma dökümü alınamadı (STT servisi yanıt vermedi veya ses yakalanmadı)._'
        : '_Tutanak bu toplantıda açılmadı._';

  const decisionsBlock =
    decisions.length > 0
      ? decisions.map((d) => `- ${d}`).join('\n')
      : '_Otomatik karar satırı bulunamadı._';

  const actionsBlock =
    actionItems.length > 0
      ? actionItems.map((d) => `- [ ] ${d}`).join('\n')
      : '_Otomatik aksiyon maddesi bulunamadı._';

  const bodyMarkdown = [
    `# TBKMeet Toplantı Tutanağı`,
    ``,
    `**Başlık:** ${input.title || 'Toplantı'}`,
    `**Tarih:** ${formatClock(input.startedAt)}`,
    `**Süre:** ${formatDuration(durationSeconds)}`,
    `**Toplantı sahibi:** ${input.hostName || '—'}`,
    `**Katılımcılar:** ${input.participants.join(', ') || '—'}`,
    `**Toplantı kodu:** ${input.meetingCode}`,
    ``,
    `## Gündem`,
    agendaBlock,
    ``,
    `## Görüşme dökümü`,
    transcriptBlock,
    ``,
    `## Kararlar`,
    decisionsBlock,
    ``,
    `## Aksiyonlar`,
    actionsBlock,
  ].join('\n');

  return {
    id: `summary_${input.meetingCode}_${input.endedAt}`,
    meetingId: input.meetingId,
    meetingCode: input.meetingCode,
    title: input.title || 'Toplantı',
    hostName: input.hostName || '',
    participants: input.participants,
    startedAt: input.startedAt,
    endedAt: input.endedAt,
    durationSeconds,
    agenda: input.agenda,
    transcript: input.transcript,
    decisions,
    actionItems,
    bodyMarkdown,
    notes: input.notes || '',
    minutesEnabled: input.minutesEnabled,
    generatedAt: Date.now(),
    source: 'local-minutes-v1',
  };
}
